import { useState, useEffect } from 'react';
import { Scenario, ScenarioEntity } from '../../types/scenario';
import { scenarioClient } from '../../util/scenarioClient';

// Hook for loading scenario from ID (API call)
export const useLoadScenarioFromId = (scenarioId: string | null) => {
  const [scenario, setScenario] = useState<Scenario | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!scenarioId) {
      setScenario(null);
      setError(null);
      return;
    }

    const loadScenario = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await scenarioClient.get(`/scenario/${scenarioId}`);
        const scenarioData: ScenarioEntity = response.data;

        // Extract the scenario data and convert date strings to Date objects
        const scenarioObj = scenarioData.data;
        if (scenarioObj.patient?.dateOfBirth) {
          scenarioObj.patient.dateOfBirth = new Date(
            scenarioObj.patient.dateOfBirth,
          );
        }
        if (scenarioObj.consultation?.date) {
          scenarioObj.consultation.date = new Date(
            scenarioObj.consultation.date,
          );
        }

        setScenario(scenarioObj);
      } catch (err) {
        setScenario(null);
        setError(
          err instanceof Error ? err.message : 'Failed to load scenario',
        );
      } finally {
        setIsLoading(false);
      }
    };

    loadScenario();
  }, [scenarioId]);

  return { scenario, isLoading, error };
};

// Hook for loading scenario from JSON text
export const useLoadScenarioFromText = (scenarioText: string | null) => {
  const [scenario, setScenario] = useState<Scenario | null>(null);
  const [scenarioName, setScenarioName] = useState<string>('');
  const [scenarioDescription, setScenarioDescription] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!scenarioText) {
      setScenario(null);
      setScenarioName('');
      setScenarioDescription('');
      setError(null);
      return;
    }

    const loadScenario = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Parse the JSON string
        const scenarioData = JSON.parse(scenarioText);

        // Extract name and description if available
        const name = scenarioData.name || '';
        const description = scenarioData.description || '';

        // Handle both full ScenarioEntity objects and just the data portion
        const scenarioObj = scenarioData.data || scenarioData;

        // Convert date strings to Date objects if they exist
        if (scenarioObj.patient?.dateOfBirth) {
          scenarioObj.patient.dateOfBirth = new Date(
            scenarioObj.patient.dateOfBirth,
          );
        }
        if (scenarioObj.consultation?.date) {
          scenarioObj.consultation.date = new Date(
            scenarioObj.consultation.date,
          );
        }

        setScenario(scenarioObj);
        setScenarioName(name);
        setScenarioDescription(description);
      } catch (err) {
        setScenario(null);
        setScenarioName('');
        setScenarioDescription('');
        setError(
          err instanceof Error ? err.message : 'Failed to parse scenario JSON',
        );
      } finally {
        setIsLoading(false);
      }
    };

    loadScenario();
  }, [scenarioText]);

  return { scenario, scenarioName, scenarioDescription, isLoading, error };
};

// Keep the old hook for backward compatibility (deprecated)
export const useLoadScenario = (scenarioIdOrText: string | null) => {
  // Try to determine if it's an ID or JSON text
  // If it starts with '{' or '[', it's likely JSON
  // Otherwise, treat it as an ID
  const isJson =
    scenarioIdOrText &&
    (scenarioIdOrText.trim().startsWith('{') ||
      scenarioIdOrText.trim().startsWith('['));

  if (isJson) {
    return useLoadScenarioFromText(scenarioIdOrText);
  } else {
    return useLoadScenarioFromId(scenarioIdOrText);
  }
};
